import { useEffect, useRef, useState, FC } from 'react';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { Design, Size } from '@Components/designConstants';
import './SecondaryNav.scss';
import { ModelConfig } from './hroas-types';

interface SecondaryNavProps {
  models: ModelConfig[];
  // False while the run's final model set is still resolving. The seeded default
  // models render synchronously, but a new-format run reconciles that set once its
  // bundle loads; showing the defaults in the meantime causes a toggle to flash in
  // and then vanish. Hold the toggles back until the set is final.
  modelsReady?: boolean;
  onModelsChange: (models: ModelConfig[]) => void;
  onEditModel?: (modelId: string) => void;
}

export const SecondaryNav: FC<SecondaryNavProps> = ({
  models,
  modelsReady = true,
  onModelsChange,
  onEditModel: _onEditModel
}) => {
  // Optimistic local state — updates immediately on each click so rapid toggles
  // never overwrite each other (avoids reading stale prop in handleToggle)
  const [localModels, setLocalModels] = useState<ModelConfig[]>(models);
  const [loadingModelIds, setLoadingModelIds] = useState<Set<string>>(new Set());

  // Refs give synchronous access inside setTimeout callbacks and effects
  const intendedRef = useRef<ModelConfig[]>(models);
  const loadingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Clear spinners for any model whose parent-confirmed state now matches intent
    const nowConfirmed = new Set<string>();
    models.forEach(m => {
      const intended = intendedRef.current.find(im => im.id === m.id);
      if (loadingRef.current.has(m.id) && intended && m.enabled === intended.enabled) {
        nowConfirmed.add(m.id);
      }
    });

    if (nowConfirmed.size > 0) {
      const next = new Set(Array.from(loadingRef.current).filter(id => !nowConfirmed.has(id)));
      loadingRef.current = next;
      setLoadingModelIds(next);
    }

    // Reconcile to the PROP's model set (add/remove aware): iterate over `models`,
    // not the previous local list, so a model the prop dropped (e.g. Geo-MMM for
    // `away`) is removed and a model the prop introduces is added. Optimistic
    // `enabled` state is preserved only for in-flight ids via loadingRef.
    intendedRef.current = models.map(m =>
      loadingRef.current.has(m.id) ? (intendedRef.current.find(im => im.id === m.id) ?? m) : m
    );

    setLocalModels(prev =>
      models.map(fromProp => {
        const existing = prev.find(lm => lm.id === fromProp.id);
        // Keep optimistic enabled state for in-flight models; sync everything else
        return existing && loadingRef.current.has(fromProp.id)
          ? { ...fromProp, enabled: existing.enabled }
          : fromProp;
      })
    );
  }, [models]);

  const handleToggle = (id: string, newValue: boolean) => {
    // Always compute from intendedRef so rapid clicks stack correctly
    const updated = intendedRef.current.map(m => (m.id === id ? { ...m, enabled: newValue } : m));
    intendedRef.current = updated;
    setLocalModels(updated);

    const newLoading = new Set(Array.from(loadingRef.current).concat(id));
    loadingRef.current = newLoading;
    setLoadingModelIds(newLoading);

    // Defer so the spinner paints before the heavy parent recompute runs
    setTimeout(() => {
      onModelsChange(intendedRef.current);
    }, 0);
  };

  const enabledCount = localModels.filter(m => m.enabled).length;

  return (
    <div className='hroas-secondary-nav'>
      <p className='hroas-secondary-nav-label'>Models</p>
      <div className='hroas-secondary-nav-buttons'>
        {/* Toggles are withheld until the run's final model set resolves. The nav
            bar has a fixed height, so an empty buttons area holds layout stable
            (no jump) instead of flashing the seeded defaults. */}
        {modelsReady &&
          localModels.map(model => {
            const isLastEnabled = model.enabled && enabledCount === 1;
            return (
              <div
                key={model.id}
                className={`hroas-secondary-nav-button${isLastEnabled ? ' last-enabled' : ''}`}
              >
                <ToggleSwitch
                  design={Design.NEUTRAL}
                  size={Size.SM}
                  label={model.label}
                  checked={model.enabled}
                  // Spinner + grey ONLY the toggle whose request is in flight. The last-enabled
                  // toggle is also `disabled` (non-interactive) but un-greyed via SCSS below.
                  loading={loadingModelIds.has(model.id)}
                  disabled={loadingModelIds.has(model.id) || isLastEnabled}
                  onChange={setterOrValue => {
                    const newValue =
                      typeof setterOrValue === 'function'
                        ? setterOrValue(model.enabled)
                        : setterOrValue;
                    handleToggle(model.id, newValue);
                  }}
                  infoTooltipText={model.infoTooltip}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};
